import React, {createRef, ReactElement} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "../shared/StyledComponents";
import {PathComponent} from "../index";
import {COLOR_BG1} from "./App";
import {DIVIDER_BLACK, PD_LG, PD_MD, PD_SM, SZ_MD} from "../shared/dimens";
import {Button, Grid, IconButton, Typography} from "@mui/material";
import {$KTM, Action, AppPrefListener, ColorValue, KeyMetadatas} from "../shared/types";
import {
  black,
  colorAlabaster,
  colorBlue,
  colorDarkGreen,
  colorDarkPurple,
  colorEarthBlue,
  colorGreen,
  colorHighlightAlt,
  colorLightCyan,
  colorLightGreen,
  colorOrange,
  colorPastelBlue,
  colorPastelGreen,
  colorPastelOrange,
  colorPastelRed,
  colorPastelYellow,
  colorPink,
  colorPurple,
  colorRed,
  colorYellow,
  lightGray,
  white
} from "../shared/colors";
import {
  BackspaceOutlined,
  CloseOutlined,
  HelpOutlineOutlined,
  KeyboardReturnOutlined,
  MoreVertOutlined,
  SettingsOutlined,
  UndoOutlined
} from "@mui/icons-material";
import {SettingsHelper} from "./SettingsHelper";
import {AppPrefs} from "./app_prefs";
import {CODABOT_KTM} from "./types";
import {TutorialHelper} from "./TutorialHelper";
import {BaseApp} from "../shared/BaseApp";
import {ImportExportHelper} from "./ImportExportHelper";
import {GalleryHelper} from "./GalleryHelper";

enum Instruction {
  FD = "FD",
  BK = "BK",
  RT = "RT",
  LT = "LT",
  PD = "PD",
  PU = "PU",
  REPEAT = "REPEAT",
  PEN = "PEN",
  END = "END",
}

enum Canvas {
  SETBG = "SETBG",
  CLS = "CLS",
  SETX = "SETX",
  SETY = "SETY",
}

enum Digit {
  D1 = "1",
  D2 = "2",
  D3 = "3",
  D4 = "4",
  D5 = "5",
  D6 = "6",
  D7 = "7",
  D8 = "8",
  D9 = "9",
  D0 = "0",
}

enum App {
  GALLERY = "GALLERY",
  RESET = "RESET",
}

enum TokenType {
  INSTRUCTION,
  DIGIT,
  NUMBER,
  CANVAS,
}

type Token = {
  type: TokenType,
  value: Instruction | Digit | Canvas | number,
}

type Command = {
  tokens: Token[],
  repeat?: Command[],
}

type Program = {
  commands: Command[],
}

function NextDigit(token: Token): boolean {
  switch (token?.value) {
    case Instruction.FD:
    case Instruction.BK:
    case Instruction.LT:
    case Instruction.RT:
    case Instruction.PEN:
    case Instruction.REPEAT:
    case Canvas.SETBG:
    case Canvas.SETX:
    case Canvas.SETY:
      return true;
    case Instruction.PD:
    case Instruction.PU:
    case Instruction.END:
    case Canvas.CLS:
      return false;
    case Digit.D1:
    case Digit.D2:
    case Digit.D3:
    case Digit.D4:
    case Digit.D5:
    case Digit.D6:
    case Digit.D7:
    case Digit.D8:
    case Digit.D9:
    case Digit.D0:
      return true;
  }
  return false;
}

function Tokenize(code: string): Token[] {
  const strs: string[] = code.trim().split(/\s+/);
  const tokens: Token[] = [];
  for (const str of strs) {
    const text = str.trim().toUpperCase();
    let instruction: Instruction;
    for (const name of Object.getOwnPropertyNames(Instruction)) {
      if (name === text) {
        instruction = Instruction[name];
        break;
      }
    }
    if (instruction) {
      tokens.push({
        type: TokenType.INSTRUCTION,
        value: instruction,
      });
      continue;
    }
    let canvas: Canvas;
    for (const name of Object.getOwnPropertyNames(Canvas)) {
      if (name === text) {
        canvas = Canvas[name];
        break;
      }
    }
    if (canvas) {
      tokens.push({
        type: TokenType.CANVAS,
        value: canvas,
      });
      continue;
    }
    try {
      const num = Number.parseInt(str);
      if (Number.isInteger(num)) {
        tokens.push({
          type: TokenType.NUMBER,
          value: num,
        });
      }
    } catch (e) {
      // Ignore.
    }
  }
  return tokens;
}

function ParseCommands(tokens: Token[]): Command[] {
  const commands: Command[] = [];
  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];
    switch (token.value) {
      case Instruction.FD:
      case Instruction.BK:
      case Instruction.LT:
      case Instruction.RT:
      case Instruction.PEN:
      case Instruction.REPEAT:
      case Canvas.SETBG:
      case Canvas.SETX:
      case Canvas.SETY:
        let n: number;
        if (tokens[(n = (i + 1))].type === TokenType.NUMBER) {
          commands.push({tokens: [token, tokens[n]]})
          i = n + 1;
          break;
        }
        let j = 0;
        let numStr = "";
        while ((n = i + j + 1) < tokens.length && tokens[n].type === TokenType.DIGIT) {
          numStr += tokens[n].value;
          j++;
        }
        if (j > 0) {
          const numToken: Token = {
            type: TokenType.NUMBER,
            value: Number.parseInt(numStr),
          };
          i = i + j + 1;
          commands.push({tokens: [token, numToken]});
          break;
        }
        throw new Error("Parse error");
      case Instruction.PD:
      case Instruction.END:
      case Instruction.PU:
      case Canvas.CLS:
        commands.push({tokens: [tokens[i++]]});
        break;
      default:
        throw new Error("Parse error");
    }
  }
  return commands;
}

type CompileResult = {
  commands: Command[],
  index: number,
}

function CompileCommands(commands: Command[], index: number = 0): CompileResult {
  const compiled: Command[] = [];
  let i = index;
  while (i < commands.length) {
    const command = commands[i];
    const token = command.tokens[0];
    if (token.value === Instruction.REPEAT) {
      const result = CompileCommands(commands, i + 1);
      compiled.push({tokens: command.tokens, repeat: result.commands});
      i = result.index;
    } else if (token.value === Instruction.END) {
      return {commands: compiled, index: i + 1};
    } else {
      compiled.push(command);
      i++;
    }
  }
  return {commands: compiled, index: i};
}

type AnimationFrame = {
  x: number,
  y: number,
  angle: number,
  penUp?: boolean,
  penColor?: string,
  bgColor?: string,
  cls?: boolean,
  noanimate?: boolean,
}

let executionCanceled: boolean = false;

async function ExecutePrograms(programs: Program[], canvas: HTMLCanvasElement, bgCanvas: HTMLCanvasElement, sprite: HTMLImageElement, noanimate?: boolean): Promise<void> {
  executionCanceled = false;
  let x: number = canvas.width / 2;
  let y: number = canvas.height / 2;
  let angle: number = Math.PI / 2;
  let penUp: boolean = false;
  let penColor: string | undefined = black;
  let bgColor: string | undefined = white;
  let cls: boolean | undefined = undefined;
  const frames: AnimationFrame[] = [];
  frames.push({x, y, angle, penColor, bgColor});
  let programIndex = 0;
  const commandsQueue: Command[] = [];
  for (const program of programs) {
    for (const command_ of CompileCommands(program.commands).commands) {
      commandsQueue.push(command_);
      while (commandsQueue.length > 0) {
        const command = commandsQueue.splice(0, 1)[0];
        const token = command.tokens[0];
        if (token.value === Instruction.REPEAT) {
          let repeatCount = command.tokens[1].value as number;
          while (repeatCount-- > 0) {
            commandsQueue.push(...command.repeat);
          }
        }
        let sDistance = 0, sAngle = 0, unit = 1;
        let nextNumber: boolean = false;
        switch (token.value) {
          case Instruction.FD:
            sDistance = -1;
            unit = 1;
            nextNumber = true;
            break;
          case Instruction.BK:
            sDistance = 1;
            unit = 1;
            nextNumber = true;
            break;
          case Instruction.LT:
            sAngle = -1;
            unit = Math.PI / 30;
            nextNumber = true;
            break;
          case Instruction.RT:
            sAngle = 1;
            unit = Math.PI / 30;
            nextNumber = true;
            break;
          case Instruction.PEN:
          case Canvas.SETBG:
          case Canvas.SETX:
          case Canvas.SETY:
            nextNumber = true;
            break;
          case Instruction.PU:
            penUp = true;
            break;
          case Instruction.PD:
            penUp = false;
            break;
          case Canvas.CLS:
            cls = true;
            break;
        }
        let dx = 0, dy = 0, dAngle = 0, steps = 1;
        if (nextNumber) {
          unit *= AppPrefs.getInstance().getSpeed();
          switch (command.tokens[1].type) {
            case TokenType.NUMBER:
              const value = command.tokens[1].value as number;
              switch (token.value) {
                case Instruction.FD:
                case Instruction.BK:
                case Instruction.LT:
                case Instruction.RT:
                  dx = sDistance * value * Math.cos(angle);
                  dy = sDistance * value * Math.sin(angle);
                  dAngle = sAngle * value * Math.PI / 180;
                  steps = Math.floor(value / unit);
                  break;
                case Instruction.PEN:
                  penColor = value >= 0 && value <= 9 ? PEN_COLORS[value] : black;
                  break;
                case Canvas.SETBG:
                  bgColor = value >= 0 && value <= 9 ? BG_COLORS[value] : white;
                  break;
                case Canvas.SETX:
                  dx = value - x;
                  steps = Math.floor(value / unit);
                  break;
                case Canvas.SETY:
                  dy = value - y;
                  steps = Math.floor(value / unit);
                  break;
              }
              break;
          }
        }
        if (programIndex === programs.length - 1 && !noanimate) {
          for (let i = 1; i < steps; i++) {
            frames.push({
              x: x + i * dx / steps,
              y: y + i * dy / steps,
              angle: angle + i * dAngle / steps,
              penUp: penUp,
            });
          }
        }
        x += dx;
        y += dy;
        angle += dAngle;
        frames.push({x, y, angle, penUp, penColor, bgColor, cls, noanimate: true});
        if (penColor) {
          penColor = undefined;
        }
        if (bgColor) {
          bgColor = undefined;
        }
        if (Boolean(cls)) {
          cls = undefined;
        }
      }
    }
    programIndex++;
  }
  return new Promise<void>(resolve => {
    DrawAnimationFrame(canvas, bgCanvas, sprite, frames, 0, resolve);
  });
}

function GetSpriteIndexForAngle(angle: number): number[] {
  while (angle < 0) {
    angle += 2 * Math.PI;
  }
  const spriteCount = 24;
  const interval = 2 * Math.PI / spriteCount;
  const index = Math.floor((angle + interval / 2) / interval) % spriteCount;
  return [index, angle - index * interval];
}

function StopExecution(): void {
  executionCanceled = true;
}

const PEN_COLORS: string[] = [
  black,
  colorPink,
  colorLightGreen,
  colorDarkGreen,
  colorYellow,
  colorRed,
  colorBlue,
  colorDarkPurple,
  colorPurple,
  colorOrange,
];

const BG_COLORS: string[] = [
  white,
  colorPastelYellow,
  colorPastelOrange,
  colorPastelRed,
  colorPastelGreen,
  colorPastelBlue,
  colorLightCyan,
  lightGray,
  colorAlabaster,
  black,
];

function DrawAnimationFrame(canvas: HTMLCanvasElement, bgCanvas: HTMLCanvasElement, sprite: HTMLImageElement, frames: AnimationFrame[], index: number, resolve: () => void): void {
  if (index >= frames.length || executionCanceled) {
    resolve();
    return;
  }
  const bgContext: CanvasRenderingContext2D = bgCanvas.getContext("2d");
  const context: CanvasRenderingContext2D = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.lineWidth = 4;
  context.beginPath();
  context.moveTo(frames[0].x, frames[0].y);
  context.strokeStyle = frames[0].penColor || black;
  bgContext.fillStyle = frames[0].bgColor || white;
  bgContext.fillRect(0, 0, canvas.width, canvas.height);
  for (let i = 1; i <= index; i++) {
    const frame = frames[i];
    if (frame.bgColor) {
      bgContext.fillStyle = frame.bgColor;
      bgContext.fillRect(0, 0, canvas.width, canvas.height);
    }
    if (frame.penUp) {
      context.moveTo(frame.x, frame.y);
    } else {
      context.lineTo(frame.x, frame.y);
    }
    if (frame.penColor) {
      context.stroke();
      context.closePath();
      context.beginPath();
      context.moveTo(frame.x, frame.y);
      context.strokeStyle = frame.penColor;
    }
    if (frame.cls) {
      context.stroke();
      context.closePath();
      context.beginPath();
      context.moveTo(frame.x, frame.y);
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
  context.stroke();
  context.closePath();
  const spriteCols = 5, spriteRows = 5;
  const sw = sprite.width / spriteCols;
  const sh = sprite.height / spriteRows;
  const ss = 0.5;
  const frame = frames[index];
  const spriteIndex = GetSpriteIndexForAngle(frame.angle - Math.PI / 2);
  const tileX = spriteIndex[0] % 5;
  const tileY = Math.floor(spriteIndex[0] / 5);
  context.save();
  context.translate(frame.x, frame.y);
  context.rotate(spriteIndex[1]);
  context.drawImage(sprite, sw * tileX, sh * tileY, sw, sh, -sw * ss / 2, -sh * ss / 2, sw * ss, sh * ss);
  context.restore();
  if (frame.noanimate) {
    DrawAnimationFrame(canvas, bgCanvas, sprite, frames, index + 1, resolve);
  } else {
    requestAnimationFrame(() => DrawAnimationFrame(canvas, bgCanvas, sprite, frames, index + 1, resolve));
  }
}

export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  running?: boolean,
  history: Program[],
  current: Token[],
  codabotSprite: HTMLImageElement,
}

type ButtonAction = {
  text: string,
  color?: ColorValue,
}

function InstructionButton(props: { disabled?: boolean, action: ButtonAction, onClick: () => void }): ReactElement {
  const action = props.action;
  return <Button
    disabled={props.disabled}
    onClick={props.onClick}
    variant="contained"
    style={{
      opacity: props.disabled ? 0.5 : 1,
      height: 48,
      border: DIVIDER_BLACK,
      background: action.color.background,
      color: action.color.color
    }}>
    <Typography variant="h6">{action.text}</Typography>
  </Button>
}

function DigitButton(props: { disabled?: boolean, action: ButtonAction, onClick: () => void }): ReactElement {
  const action = props.action;
  return <Button
    disabled={props.disabled}
    onClick={props.onClick}
    style={{
      opacity: props.disabled ? 0.5 : 1,
      height: 48,
      border: DIVIDER_BLACK,
    }}>
    <StyledBoxRow style={{alignItems: "center"}}>
      <Typography variant="h6">{action.text}</Typography>
      <div style={{width: 16, height: 16, borderRadius: 8, background: action.color.color}}/>
      <div
        style={{width: 16, height: 16, borderRadius: 8, border: DIVIDER_BLACK, background: action.color.background}}/>
    </StyledBoxRow>
  </Button>;
}

function ActionButton(props: { disabled?: boolean, action: ButtonAction, onClick: () => void }): ReactElement {
  const action = props.action;
  return <div style={{width: "100%", padding: PD_SM}}>
    <Button
      disabled={props.disabled}
      onClick={props.onClick}
      style={{
        opacity: props.disabled ? 0.5 : 1,
        height: 32,
        width: "100%",
        border: DIVIDER_BLACK,
      }}>
      <StyledBoxRow style={{alignItems: "center"}}>
        <Typography variant="h6">{action.text}</Typography>
      </StyledBoxRow>
    </Button>
  </div>
}

export class Main extends BaseFragment<MainProps, MainState> implements AppPrefListener {

  private readonly instructionActions = new KeyMetadatas<ButtonAction>([
    $KTM(Instruction.FD, {text: "FD", color: new ColorValue("white", colorRed)}),
    $KTM(Instruction.BK, {text: "BK", color: new ColorValue("white", colorOrange)}),
    $KTM(Instruction.RT, {text: "RT", color: new ColorValue("white", colorYellow)}),
    $KTM(Instruction.LT, {text: "LT", color: new ColorValue("white", colorLightGreen)}),
    $KTM(Instruction.PD, {text: "PEN D", color: new ColorValue("white", colorGreen)}),
    $KTM(Instruction.PU, {text: "PEN U", color: new ColorValue("white", colorBlue)}),
    $KTM(Instruction.REPEAT, {text: "REPEAT", color: new ColorValue("white", colorEarthBlue)}),
    $KTM(Instruction.PEN, {text: "PEN", color: new ColorValue("black", "white")}),
    $KTM(Instruction.END, {text: "END", color: new ColorValue("white", colorPurple)}),
  ]);

  private readonly digitActions = new KeyMetadatas<ButtonAction>([
    $KTM(Digit.D1, {text: "1", color: new ColorValue(PEN_COLORS[1], BG_COLORS[1])}),
    $KTM(Digit.D2, {text: "2", color: new ColorValue(PEN_COLORS[2], BG_COLORS[2])}),
    $KTM(Digit.D3, {text: "3", color: new ColorValue(PEN_COLORS[3], BG_COLORS[3])}),
    $KTM(Digit.D4, {text: "4", color: new ColorValue(PEN_COLORS[4], BG_COLORS[4])}),
    $KTM(Digit.D5, {text: "5", color: new ColorValue(PEN_COLORS[5], BG_COLORS[5])}),
    $KTM(Digit.D6, {text: "6", color: new ColorValue(PEN_COLORS[6], BG_COLORS[6])}),
    $KTM(Digit.D7, {text: "7", color: new ColorValue(PEN_COLORS[7], BG_COLORS[7])}),
    $KTM(Digit.D8, {text: "8", color: new ColorValue(PEN_COLORS[8], BG_COLORS[8])}),
    $KTM(Digit.D9, {text: "9", color: new ColorValue(PEN_COLORS[9], BG_COLORS[9])}),
    $KTM(Digit.D0, {text: "0", color: new ColorValue(PEN_COLORS[0], BG_COLORS[0])}),
  ]);

  private readonly canvasActions = new KeyMetadatas<ButtonAction>([
    $KTM(Canvas.SETBG, {text: "SET BG"}),
    $KTM(Canvas.CLS, {text: "CLS"}),
    $KTM(Canvas.SETX, {text: "SET X"}),
    $KTM(Canvas.SETY, {text: "SET Y"}),
  ]);

  private readonly appActions = new KeyMetadatas<ButtonAction>([
    $KTM(App.GALLERY, {text: "Gallery"}),
    $KTM(App.RESET, {text: "Reset"}),
    // {text: "Save"},
    // {text: "Fullscreen"},
    // {text: "Quit"},
  ]);

  private readonly canvasParentRef = createRef<HTMLDivElement>();
  private readonly bgCanvasRef = createRef<HTMLCanvasElement>();
  private readonly canvasRef = createRef<HTMLCanvasElement>();

  static nestedPaths(): PathComponent[] {
    return [];
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    await this.loadCodabotSprite();
  }

  private async loadCodabotSprite(): Promise<void> {
    const codabot = AppPrefs.getInstance().getCodabot();
    const metadata = CODABOT_KTM.findMetadata(codabot);
    this.setState({
      codabotSprite: await new Promise<HTMLImageElement>(resolve => {
        const img = new Image();
        img.addEventListener("load", () => {
          resolve(img);
        });
        img.src = metadata.spriteImageUrl;
      }),
    });
  }

  componentDidUpdate(prevProps: Readonly<MainProps>, prevState: Readonly<MainState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (!prevState.codabotSprite && this.state.codabotSprite) {
      ExecutePrograms(this.state.history, this.canvasRef.current, this.bgCanvasRef.current, this.state.codabotSprite);
      this.setState({
        history: [
          {
            commands:
              [
                // ...(new Array(24).fill(null).flatMap(_ => ([
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.FD}, {type: TokenType.NUMBER, value: 30}],
                //   },
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.RT}, {type: TokenType.NUMBER, value: 15}],
                //   },
                // ]))),
                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.PEN}, {type: TokenType.NUMBER, value: 1}],
                // },
                // ...(new Array(24).fill(null).flatMap(_ => ([
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.FD}, {type: TokenType.NUMBER, value: 30}],
                //   },
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.LT}, {type: TokenType.NUMBER, value: 15}],
                //   },
                // ]))),
                // {
                //   tokens: [{type: TokenType.CANVAS, value: Canvas.SETBG}, {
                //     type: TokenType.NUMBER,
                //     value: 4,
                //   }],
                // },
                // {
                //   tokens: [{type: TokenType.CANVAS, value: Canvas.CLS}],
                // },
                // ...(new Array(24).fill(null).flatMap(_ => ([
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.FD}, {type: TokenType.NUMBER, value: 30}],
                //   },
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.RT}, {type: TokenType.NUMBER, value: 15}],
                //   },
                // ]))),
                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.PEN}, {type: TokenType.NUMBER, value: 2}],
                // },
                // ...(new Array(24).fill(null).flatMap(_ => ([
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.FD}, {type: TokenType.NUMBER, value: 30}],
                //   },
                //   {
                //     tokens: [{type: TokenType.INSTRUCTION, value: Instruction.LT}, {type: TokenType.NUMBER, value: 15}],
                //   },
                // ]))),


                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.REPEAT}, {
                //     type: TokenType.NUMBER,
                //     value: 4
                //   }],
                // },
                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.FD}, {type: TokenType.NUMBER, value: 30}],
                // },
                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.LT}, {type: TokenType.NUMBER, value: 15}],
                // },
                // {
                //   tokens: [{type: TokenType.INSTRUCTION, value: Instruction.END}],
                // },
              ],
          },
        ],
      });
    } else if (prevState.codabotSprite && (prevState.codabotSprite !== this.state.codabotSprite)) {
      ExecutePrograms(this.state.history, this.canvasRef.current, this.bgCanvasRef.current, this.state.codabotSprite);
    } else if (prevState.history.length > this.state.history.length) {
      ExecutePrograms(this.state.history.slice(0, this.state.history.length - 1), this.canvasRef.current, this.bgCanvasRef.current, this.state.codabotSprite, true);
    }
  }

  protected onCreateState(): MainState {
    return {
      ...super.onCreateState(),
      history: [
        {
          commands: [],
        },
      ],
      current: [],
    };
  }

  componentDidMount() {
    super.componentDidMount();
    AppPrefs.getInstance().addListener(AppPrefs.PREF_CODABOT, this);
    setTimeout(() => {
      TutorialHelper.showTutorial(true);
    }, 1000);
  }

  componentWillUnmount() {
    AppPrefs.getInstance().removeListener(AppPrefs.PREF_CODABOT, this);
    super.componentWillUnmount();
  }

  onPrefChanged(key: string) {
    switch (key) {
      case AppPrefs.PREF_CODABOT:
        this.loadCodabotSprite();
        break;
    }
  }

  private getCurrentProgram(): Command[] {
    const history = this.state.history;
    return history[history.length - 1].commands;
  }

  private addCurrentCommand(): void {
    const history = this.state.history;
    const program = history[history.length - 1].commands;
    this.setState({
      history: [
        ...history.slice(0, history.length - 1),
        {
          commands: [
            ...program,
            ...ParseCommands(this.state.current),
          ],
        }],
      current: [],
    });
  }

  private canDeleteCurrentCommand(): boolean {
    const history = this.state.history;
    const program = history[history.length - 1].commands;
    return program.length > 0;
  }

  private deleteCurrentCommand(): void {
    const history = this.state.history;
    const program = history[history.length - 1].commands;
    this.setState({
      history: [
        ...history.slice(0, history.length - 1),
        {
          commands: [
            ...program.slice(0, program.length - 1),
          ],
        }],
    });
  }

  private undo(): void {
    this.setState({
      history: [...this.state.history.slice(0, this.state.history.length - 1)],
    });
  }

  private importCode(code: string): void {
    this.setState({
      history: [
        {
          commands: ParseCommands(Tokenize(code)),
        },
      ],
      current: [],
    });
  }

  private exportCode(): string {
    return this.state.history.map(
      program => program.commands.map(
        command => command.tokens.map(
          token => token.value.toString())
          .join(" "))
        .join("\n"))
      .join("\n");
  }

  renderContainerContent(): React.ReactElement {
    const maxWidth = 560;
    const program = this.getCurrentProgram();
    const delDisabled = this.state.running || (!(this.state.current.length > 0) && !this.canDeleteCurrentCommand());
    const showHistory = false;
    let indent = 0;
    return <StyledBoxRow
      style={{height: "100vh", overflow: "hidden", background: COLOR_BG1, flexGrow: 1, gap: 0, position: "relative"}}>
      <StyledBoxColumn style={{width: "25%", minWidth: 240, padding: PD_MD, borderRight: DIVIDER_BLACK}}>
        <StyledBoxRow style={{width: "100%", alignItems: "center"}}>
          <img src={"/stamp.svg"} style={{width: "60%", padding: PD_LG, marginLeft: "auto", marginRight: "auto"}}/>
          <IconButton onClick={() => TutorialHelper.showTutorial()}>
            <HelpOutlineOutlined/>
          </IconButton>
          <IconButton onClick={() => SettingsHelper.showSettings()}>
            <SettingsOutlined/>
          </IconButton>
        </StyledBoxRow>
        <StyledBoxRow>
          <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1}}>
            {this.instructionActions.values.map(value => <InstructionButton
              disabled={this.state.running}
              onClick={() => this.setState({
                current: [...this.state.current, {type: TokenType.INSTRUCTION, value: value.key as Instruction}],
              })}
              action={value.metadata}/>)}
          </StyledBoxColumn>
          <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1}}>
            {this.digitActions.values.map(value => <DigitButton
              onClick={() => this.setState({
                current: [...this.state.current, {type: TokenType.DIGIT, value: value.key as Digit}],
              })}
              disabled={!NextDigit(this.state.current[this.state.current.length - 1])}
              action={value.metadata}/>)}
          </StyledBoxColumn>
        </StyledBoxRow>
      </StyledBoxColumn>
      <StyledBoxColumn style={{flexGrow: 1, gap: 0}}>
        <StyledBoxRow style={{gap: 0, overflow: "hidden"}}>
          <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1}}>
            <StyledBoxRow style={{padding: PD_LG}}>
              {!this.state.running
                ? <Button
                  disabled={this.state.running || (this.state.history.length === 1 && this.state.history[this.state.history.length - 1].commands.length === 0)}
                  onClick={() => {
                    if (!(this.state.history[this.state.history.length - 1].commands.length > 0)) {
                      return;
                    }
                    this.setState({
                      running: true,
                    })
                    const self = this;
                    (async function () {
                      await ExecutePrograms(self.state.history, self.canvasRef.current, self.bgCanvasRef.current, self.state.codabotSprite);
                      if (executionCanceled) {
                        executionCanceled = false;
                        await ExecutePrograms([...self.state.history.slice(0, self.state.history.length - 1)], self.canvasRef.current, self.bgCanvasRef.current, self.state.codabotSprite, true);
                      } else {
                        self.setState({
                          running: false,
                          history: [
                            ...self.state.history,
                            {
                              commands: [],
                            },
                          ],
                          current: [],
                        });
                      }
                    })();
                  }}
                  style={{fontSize: "100%", padding: PD_LG, width: "100%"}}
                  variant="contained">Click to RUN (DRAW)</Button>
                : null}
              {this.state.running
                ? <Button
                  color={"error"}
                  disabled={!this.state.running}
                  onClick={() => {
                    this.setState({
                      running: false,
                    });
                    StopExecution();
                  }}
                  style={{fontSize: "100%", padding: PD_LG, width: "100%"}}
                  variant="contained">STOP</Button>
                : null}
            </StyledBoxRow>
            <StyledBoxRow style={{padding: PD_LG, marginTop: -PD_LG}}>
              <Button
                onClick={() => this.undo()}
                disabled={!(this.state.history.length > 1)}
                startIcon={<UndoOutlined/>}>
                Undo
              </Button>
              {showHistory ?
                <Button>
                  <StyledBoxRow>
                    <div style={{
                      display: "flex",
                      width: 28,
                      aspectRatio: 1,
                      borderRadius: 14,
                      alignItems: "center",
                      justifyContent: "center",
                      border: DIVIDER_BLACK,
                    }}><Typography variant={"caption"}>{this.state.history.length}</Typography></div>
                    Show history
                  </StyledBoxRow>
                </Button>
                : null}
              <StyledSpan/>
              <Button
                onClick={(event) => BaseApp.CONTEXT.showActionsListPopover(event.target as HTMLElement, null, [
                  new Action("Import", () => ImportExportHelper.showImport(code => this.importCode(code))),
                  new Action("Export", () => ImportExportHelper.showExport(this.exportCode())),
                ])}>
                <MoreVertOutlined/>
              </Button>
            </StyledBoxRow>
            <StyledBoxColumn style={{
              flexGrow: 1,
              background: lightGray,
              margin: PD_LG,
              marginTop: -PD_LG,
              border: DIVIDER_BLACK,
              overflowY: "scroll"
            }}>
              {program.map((command, index) => {
                const token = command.tokens[0];
                if (token.value === Instruction.END) {
                  indent--;
                }
                const element = <StyledBoxRow
                  style={{
                    padding: PD_SM,
                    paddingLeft: PD_SM + indent * PD_LG,
                    background: index === program.length - 1 ? colorHighlightAlt : null
                  }}>
                  {command.tokens.map(token => <Typography variant={"h6"}>{token.value}</Typography>)}
                </StyledBoxRow>;
                if (token.value === Instruction.REPEAT) {
                  indent++;
                }
                return element;
              })}
            </StyledBoxColumn>
          </StyledBoxColumn>
          <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1.5}}>
            <div style={{display: "flex", flexGrow: 1, flexDirection: "column", padding: PD_LG, alignItems: "center"}}>
              <div style={{display: "flex", flexDirection: "column", width: "100%", maxWidth: maxWidth}}>
                <div style={{width: "100%", display: "flex"}}>
                  <Typography variant="h6">0</Typography>
                  <StyledSpan/>
                  <Typography variant="h6">500</Typography>
                </div>
                <div
                  ref={this.canvasParentRef}
                  style={{
                    width: "100%",
                    maxWidth: maxWidth,
                    aspectRatio: 1,
                    border: DIVIDER_BLACK,
                    position: "relative",
                  }}>
                  <canvas
                    ref={this.bgCanvasRef} width={maxWidth} height={maxWidth} style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    zoom: (this.canvasParentRef.current?.clientWidth / maxWidth) || 1
                  }}/>
                  <canvas ref={this.canvasRef} width={maxWidth} height={maxWidth} style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    zoom: (this.canvasParentRef.current?.clientWidth / maxWidth) || 1
                  }}/>
                </div>
              </div>
            </div>
          </StyledBoxColumn>
        </StyledBoxRow>
        <StyledBoxRow style={{height: SZ_MD, flexShrink: 0, padding: PD_LG}}>
          <Typography variant="h4" style={{
            alignSelf: "center",
            fontFamily: "monospace"
          }}>{this.state.current.map(token => token.value).join("")}</Typography>
          <div className={"cursor"}/>
          <Button
            variant={"contained"}
            onClick={() => this.addCurrentCommand()}
            style={{background: colorDarkGreen, color: "white"}}>
            <StyledBoxRow style={{alignItems: "center"}}>
              <KeyboardReturnOutlined/>
              <Typography variant={"h6"}>ENTER</Typography>
            </StyledBoxRow>
          </Button>
          <Button
            disabled={delDisabled}
            variant={"contained"}
            onClick={() => {
              if (this.state.current.length > 0) {
                this.setState({current: [...this.state.current.slice(0, this.state.current.length - 1)]});
              } else if (this.canDeleteCurrentCommand()) {
                this.deleteCurrentCommand();
              }
            }}
            style={{background: "black", color: "white", opacity: delDisabled ? 0.5 : 1}}>
            <StyledBoxRow style={{alignItems: "center"}}>
              <BackspaceOutlined/>
              <Typography variant={"h6"}>DEL</Typography>
            </StyledBoxRow>
          </Button>
          <StyledSpan/>
          <Button
            variant={"contained"}
            onClick={() => this.setState({current: []})}
            style={{background: "black", color: "white"}}>
            <StyledBoxRow style={{alignItems: "center"}}>
              <CloseOutlined/>
              <Typography variant={"h6"}>CLEAR</Typography>
            </StyledBoxRow>
          </Button>
        </StyledBoxRow>
        <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1, flexShrink: 0, borderTop: DIVIDER_BLACK, padding: PD_SM}}>
          <Grid container>
            {this.canvasActions.values.map(value => <Grid xs={6} sm={4} md={3}>
              <ActionButton
                disabled={this.state.running}
                onClick={() => this.setState({
                  current: [...this.state.current, {type: TokenType.CANVAS, value: value.key as Canvas}],
                })}
                action={value.metadata}/>
            </Grid>)}
          </Grid>
          <StyledSpan/>
          <Grid container>
            {this.appActions.values.map(value => <Grid xs={12} sm={6} md={4} lg={3}>
              <ActionButton
                disabled={this.state.running}
                onClick={() => {
                  switch (value.key) {
                    case App.GALLERY:
                      GalleryHelper.showGallery(code => this.importCode(code));
                      break;
                    case App.RESET:
                      this.setState({
                        history: [
                          {
                            commands: [],
                          },
                        ],
                        current: [],
                      });
                      break;
                  }
                }}
                action={value.metadata}/>
            </Grid>)}
          </Grid>
        </StyledBoxColumn>
      </StyledBoxColumn>
    </StyledBoxRow>
  }
}
